// Browser-side half of hosted sign-in (SSO and MFA).
//
// Copied verbatim from boomr/boomr-lib/hostedLoginClient.ts, the TypeScript port
// of boomr-lib/hostedLoginClient.js; a boomr-lib test keeps the two in step and
// boomr/docs/SingleSignOn.md has the contract with api-v2. The copies live in
// alznext, audalize-cms, audalize-manage-cloud and boomr-deploy: change the
// boomr-lib original first, then re-copy it to all four.
//
//   1. loginWithPassword fails with one of HOSTED_LOGIN_CODES. The error's
//      extensions say where to go: `startUrl` for SSO_REQUIRED, `redirectUrl`
//      for the two MFA codes.
//   2. The app appends its own callback as `redirectUri` and navigates there.
//      api-v2 only honours callbacks on its allowlist, all at /ssoCallback.
//   3. The hosted pages finish on <callback>#audCode=<one-time code> or
//      #audError=<public code>; exchange the code with completeHostedLogin.

export const HOSTED_LOGIN_CODES = ['SSO_REQUIRED', 'MFA_REQUIRED', 'MFA_ENROLLMENT_REQUIRED'];

export const HANDOFF_FRAGMENT_KEY = 'audCode';
export const ERROR_FRAGMENT_KEY = 'audError';

export const PUBLIC_ERROR_MESSAGES: Record<string, string> = {
  'sso-not-configured': 'Single sign-on is not set up for this email domain.',
  'provider-error': 'Your organisation\u2019s sign-in service returned an error. Try again.',
  'account-disabled': 'This account has been disabled. Contact Audalize support.',
  'no-account': 'There is no Audalize account for this email. Ask your venue administrator for an invitation.',
  'ambiguous-account': 'This sign-in matches more than one Audalize account. Contact Audalize support.',
  'untrusted-email': 'Your organisation did not confirm this email address, so it cannot be used to sign in.',
  'email-domain-mismatch': 'You signed in to your organisation with a different email address than the one you entered.',
  'link-needs-admin-approval': 'An Audalize administrator must approve linking this account to single sign-on before you can use it.',
  'link-attempts-exhausted': 'Too many attempts to link this account. Wait a while and try again.',
  'sso-failed': 'Single sign-on could not be completed. Try again.',
  'mfa-locked': 'Too many incorrect codes. Wait an hour and try again, or contact Audalize support.',
  'mfa-expired': 'The sign-in took too long and expired. Start again.',
  'sign-in-failed': 'Sign-in could not be completed. Try again.',
};

const REFUSAL_MESSAGES: Record<string, string> = {
  ACCOUNT_DISABLED: 'This account has been disabled. Contact Audalize support.',
  SIGN_IN_REFUSED: 'Sign-in could not be completed.',
};

export interface GqlErrorLike {
  message?: string;
  extensions?: { code?: string; startUrl?: string | null; redirectUrl?: string | null; [key: string]: unknown };
}

export interface HostedLoginRedirect {
  code: string;
  /** Null when api-v2 could not say where to go; show `message` instead. */
  url: string | null;
  message: string;
}

export type HostedLoginResult = { handoffCode: string } | { error: string; message: string };

/** The GraphQL errors inside whatever a client library threw or returned. */
export function graphQLErrorsOf(err: unknown): GqlErrorLike[] {
  if (!err) return [];
  if (Array.isArray(err)) return err as GqlErrorLike[];
  const e = err as any;
  if (Array.isArray(e.graphQLErrors) && e.graphQLErrors.length) return e.graphQLErrors;
  if (Array.isArray(e.errors)) return e.errors;
  const body = e.networkError && e.networkError.result;
  if (body && Array.isArray(body.errors)) return body.errors;
  if (e.extensions) return [e as GqlErrorLike];
  return [];
}

function codeOf(e: GqlErrorLike | null | undefined): string | null {
  return (e && e.extensions && e.extensions.code) || null;
}

export function authErrorCode(err: unknown): string | null {
  for (const e of graphQLErrorsOf(err)) {
    const code = codeOf(e);
    if (code) return code;
  }
  return null;
}

export function withRedirectUri(target: string | null | undefined, callbackUrl: string | null | undefined): string | null {
  if (!target || !callbackUrl) return null;
  try {
    const url = new URL(target);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    url.searchParams.set('redirectUri', callbackUrl);
    return url.toString();
  } catch (e) {
    return null;
  }
}

/** Whether a failed sign-in must continue on a hosted page, and where; null if not. */
export function hostedLoginRedirect(err: unknown, callbackUrl: string | null): HostedLoginRedirect | null {
  for (const e of graphQLErrorsOf(err)) {
    const code = codeOf(e);
    if (!code || !HOSTED_LOGIN_CODES.includes(code)) continue;
    const ext = e.extensions || {};
    const target = code === 'SSO_REQUIRED' ? ext.startUrl : ext.redirectUrl;
    return { code, url: withRedirectUri(target, callbackUrl), message: e.message || 'Continue signing in.' };
  }
  return null;
}

/** A readable message for a non-hosted refusal (disabled account etc.), or null. */
export function authRefusalMessage(err: unknown): string | null {
  for (const e of graphQLErrorsOf(err)) {
    const code = codeOf(e);
    if (code && REFUSAL_MESSAGES[code]) return REFUSAL_MESSAGES[code];
  }
  return null;
}

export function describeHostedLoginError(publicCode: string): string {
  return PUBLIC_ERROR_MESSAGES[publicCode] || PUBLIC_ERROR_MESSAGES['sign-in-failed'];
}

/** Reads what the hosted pages put on the callback URL's fragment. */
export function readHostedLoginResult(hash: string | null | undefined): HostedLoginResult | null {
  if (!hash) return null;
  const params = new URLSearchParams(String(hash).replace(/^#/, ''));
  const handoffCode = params.get(HANDOFF_FRAGMENT_KEY);
  if (handoffCode) return { handoffCode };
  const error = params.get(ERROR_FRAGMENT_KEY);
  if (error) return { error, message: describeHostedLoginError(error) };
  return null;
}

/** Drops the fragment so a one-time code never sits in history or a copied URL. */
export function clearHostedLoginFragment(win: Window | null | undefined): void {
  if (!win || !win.location || !win.location.hash) return;
  const clean = win.location.pathname + win.location.search;
  if (win.history && typeof win.history.replaceState === 'function') {
    win.history.replaceState(win.history.state, '', clean);
  } else {
    win.location.hash = '';
  }
}

/** `<origin>/ssoCallback` for the running app; the path every allowlist entry uses. */
export function defaultCallbackUrl(win: Window | null | undefined, path = '/ssoCallback'): string | null {
  if (!win || !win.location) return null;
  return win.location.origin + path;
}
